import { action, makeAutoObservable, runInAction } from 'mobx';

import type { MainIndication } from '@/services/patientsService/types';
import type { ParameterConfig } from '@/types/measurements';

import { type IMedia } from '../components/MediaUploader/store';
import { type walkGaitParameters } from '../services/patientWalksService/types';
import { getParameterUnit } from '../utils/parametersUtil';
import { setSelectedClinicUuid } from './selected-clinic-cookie';

export interface IClinicUser {
  uuid: string;
  email: string;
  firstName: string;
  fullName?: string;
  lastName: string;
  discipline?: string;
  externalEmrId?: string;
  welcomeVideo?: IMedia;
}

export interface IClinicFeatureMap {
  rtmEnabled: boolean;
  communicationEnabled: boolean;
  homeExercisePlanEnabled: boolean;
  patientAppEnabled: boolean;
  clinicianAppEnabled: boolean;
  fallPreventionProgram: boolean;
  careEngineEnabled: boolean;
  pointclick?: boolean;
  epicEmr?: boolean;
  yardiEmr?: boolean;
  progressNote2Enabled?: boolean;
  displayDeviceAdjustment?: boolean;
  displayDeviceAdjustmentTrend?: boolean;
  enableDailyStepGoal?: boolean;
  cptCode8081MaxUnits?: number;
  usePatientNotes: boolean;
  enableExternalEmr?: boolean;
  enableMrn?: boolean;
  patientOverviewFallRiskV2?: boolean;
  simplifiedHomepage?: boolean;
  careRecommendations?: boolean;
  careRecommendationsAiSummarization?: boolean;
  patientOverviewRtmTab?: boolean;
  copyForEmrLlmEnabled?: boolean;
  enableFallriskPassiveIndicators?: boolean;
  disableFallRiskNorms?: boolean;
  experimentalSummaryEnabled?: boolean;
  reportsEnabled?: boolean;
  reportsDesignerAdminDashboardTabs?: boolean;
  multiIndicationDemoEnabled?: boolean;
  noEpisodeOfCare?: boolean;
  editableSystemRosterCards?: boolean;
  cohortAgentAuthoring?: boolean;
  fallRiskTrendGapLine?: boolean;
  eventBasedAttributes?: boolean;
  usePatientEncountersActiveView?: boolean;
  computedColumns?: boolean;
  newPatientPrimaryProgressIndication?: boolean;
  mainIndication?: MainIndication | null;
}

export interface IDefaultPatientColumns {
  [key: string]: boolean;
}

export type WidgetState = {
  value: boolean;
  isDisabled: boolean;
  defaultItem?: string;
  subItems: Record<string, boolean>;
};
export type WidgetType = boolean | WidgetState;

export interface IWidgetBlock {
  activeWidgets?: Record<string, WidgetType>;
}
export interface IWalkPrecisionSettings {
  maxPrecisionForWalks: boolean;
}
export interface IWebClientConfig {
  defaultPatientColumns: IDefaultPatientColumns;
  widgetsBlock: IWidgetBlock;
  walkPrecisionSettings?: IWalkPrecisionSettings;
}
export interface IClinicPreview {
  uuid: string;
  displayName: string;
}

export type units = '%' | '°' | 'cm' | 'km/h' | '/minute' | undefined;

export interface IGaitParameterInfo {
  gpEnum: walkGaitParameters;
  title: string;
  units: units;
  info: string;
  youtubeId: string;
  scores: [number, number];
  scoresReverse: boolean;
  maxRange?: number;
  minRange?: number;
}

export interface IAppStore {
  clinicUuid: string;
  clinicOrg: string;
  setClinic(uuid: string): void;
  gaitParams: IGaitParameterInfo[];
  updateGaitParams(metadata: ParameterConfig[], isImperial: boolean): void;
}

export default class AppStore implements IAppStore {
  clinicUuid = '';
  gaitParams: IGaitParameterInfo[] = [];
  clinicOrg = '';
  constructor() {
    makeAutoObservable(this);
  }

  setClinic(uuid: string) {
    setSelectedClinicUuid(uuid);
    runInAction(() => {
      this.clinicUuid = uuid;
    });
  }

  @action
  updateGaitParams(metadata: ParameterConfig[], isImperial: boolean) {
    runInAction(() => {
      this.gaitParams = this.gaitParams.map((param) => {
        return {
          ...param,
          units: getParameterUnit(metadata, param.gpEnum, param.units, isImperial),
        } as IGaitParameterInfo;
      });
    });
  }
}
